export const OVERDUE_GRACE_MS = 5 * 60 * 1000;

// Mirror of BRAND_CANCELLATION_FLAGS (node/scripts/brands/utils/brand_cancellation_helpers.js);
// the POST route re-validates against the server list, which is the source of truth. The
// default removal set is not mirrored — the preview response carries it as defaultFlags.
export const CATEGORY_CONFIGS = [
  {
    flag: 'remove_rates',
    label: 'ShopMy network rates',
    shortLabel: 'rates',
    effect: 'Sets the brand commission rate to 0; the merchants table reconciles automatically.',
    columns: ['name', 'domain', 'source', 'isSMSWinner']
  },
  {
    flag: 'remove_locks',
    label: 'Locked domain sources',
    shortLabel: 'locks',
    effect: 'Unlocks the shopmyshelf source on the brand domains.',
    columns: ['domain', 'source', 'isSourceLocked']
  },
  {
    flag: 'remove_codes',
    label: 'Creator codes & offers',
    shortLabel: 'codes & offers',
    effect: 'Deletes non-personal creator codes and removes public sales and unclaimed private offers. Claimed private offer codes keep their original expiration.',
    tables: [
      { key: 'codes', columns: ['displayText', 'User_id'] },
      { key: 'offers', columns: ['title', 'status'] }
    ]
  },
  {
    flag: 'remove_custom_rates',
    label: 'Custom rates',
    shortLabel: 'custom rates',
    effect: 'Deletes every custom rate on the brand.',
    columns: ['rate', 'User_id']
  },
  {
    flag: 'remove_custom_affiliate_rates',
    label: 'Custom SKU-based rates',
    shortLabel: 'SKU rates',
    effect: 'Deletes every SKU-based rate on the brand.',
    columns: ['rate', 'SpecificVariant_id', 'SpecificVariantSibling_id']
  },
  {
    flag: 'remove_registration_codes',
    label: 'Registration codes',
    shortLabel: 'registration codes',
    effect: 'Sets the use limit on each registration code to 0 — the codes themselves are kept.',
    columns: ['code', 'createdAt']
  },
  {
    flag: 'remove_outstanding_requests',
    label: 'Outstanding gifting requests',
    shortLabel: 'gifting requests',
    effect: 'Deletes gifting requests creators have not yet accepted or rejected.',
    columns: ['name', 'title', 'createdAt']
  },
  {
    flag: 'remove_outstanding_opportunity_requests',
    label: 'Outstanding opportunity requests',
    shortLabel: 'opportunity requests',
    effect: 'Deletes opportunity requests creators have not yet accepted or rejected.',
    columns: ['name', 'title', 'createdAt']
  },
  {
    flag: 'remove_scrapers',
    label: 'Scrapers',
    shortLabel: 'scrapers',
    effect: 'Deletes the scrapers on the brand domains.',
    columns: ['name', 'domain']
  },
  {
    flag: 'remove_brand_integration',
    label: 'Brand integrations',
    shortLabel: 'integrations',
    effect: 'Removes integrations without outstanding private offer claims and their matched-customer lists. Connections needed by valid claimed codes are retained with catalog syncing disabled.',
    columns: ['domain', 'type']
  }
];

// Tasks the server queues itself carry a trigger instead of a triggering user.
export const TASK_TRIGGER_LABELS = {
  subscription_cancelled: 'automatically when the subscription ended'
};

export const isCancellationCategoryEmpty = (previewData, flag) => {
  const category = previewData.categories[flag];
  // The merchants preview is a proxy — the brand can carry a live commission rate
  // with no SMS-winner merchant row, and zeroing it is still real work.
  if (flag === 'remove_rates') return !category.count && !(previewData.brand.commission_rate > 0);
  return !category.count;
};

// Defaults come from the preview response unpruned: a category that is empty today can
// still gain data before a scheduled run date, so it stays in the default set.
export const getDefaultCancellationFlags = previewData => Object.fromEntries((previewData.defaultFlags || []).map(flag => [flag, true]));

const dateTimeFmtET = new Intl.DateTimeFormat('en-US', { dateStyle: 'short', timeStyle: 'short', timeZone: 'America/New_York' });
const dateOnlyFmtET = new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeZone: 'America/New_York' });
const datePartsFmtET = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' });

export const formatTaskDateET = value => {
  const date = new Date(value);
  return isNaN(date.getTime()) ? '—' : `${dateTimeFmtET.format(date)} ET`;
};

export const formatDateOnlyET = value => {
  const date = new Date(value);
  return isNaN(date.getTime()) ? '—' : dateOnlyFmtET.format(date);
};

// Built from formatToParts rather than an en-CA format() round-trip — that locale
// emitting YYYY-MM-DD is an ICU implementation detail, not a spec guarantee.
export const getTodayDateStringET = () => {
  const parts = datePartsFmtET.formatToParts(new Date());
  const part = type => parts.find(p => p.type === type).value;
  return `${part('year')}-${part('month')}-${part('day')}`;
};
